import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, Paper, Stack, TextField, Typography, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton, Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionIcon from "@mui/icons-material/Description";
import Swal from "sweetalert2";
import { listarFichas, excluirFicha } from "../../services/fichaService";

const moeda = (v) =>
  v == null ? "-" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function FichaList() {
  const navigate = useNavigate();
  const [fichas, setFichas] = useState([]);
  const [termo, setTermo] = useState("");
  const [filtro, setFiltro] = useState("todas"); // todas | andamento | enviadas
  const [entrega, setEntrega] = useState("todas"); // todas | 1 Pendente | 2 Parcial | 3 Completa

  const carregar = async () => {
    try {
      const { data } = await listarFichas({ termo });
      setFichas(data);
    } catch (e) {
      Swal.fire("Erro", e?.response?.data || e.message, "error");
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const excluir = async (id) => {
    const r = await Swal.fire({
      title: "Excluir ficha?",
      text: "Esta ação não pode ser desfeita.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Excluir",
      cancelButtonText: "Cancelar",
    });
    if (!r.isConfirmed) return;
    await excluirFicha(id);
    carregar();
  };

  // Ficha só pode ser excluída enquanto está em preenchimento (1) — depois de enviada
  // ela é o registro da inscrição, com anexos e análise.
  const podeExcluir = (f) => f.IdSituacaoInscricao == null || f.IdSituacaoInscricao === 1;

  // Em andamento = Em preenchimento (1); Enviadas/concluídas = Enviada em diante (>=2).
  const fichasFiltradas = fichas.filter((f) => {
    if (entrega !== "todas" && String(f.IdSituacaoEntrega ?? "") !== String(entrega)) return false;
    if (filtro === "andamento") return f.IdSituacaoInscricao === 1;
    if (filtro === "enviadas") return f.IdSituacaoInscricao >= 2;
    return true;
  });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Fichas Socioeconômicas</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={() => navigate("/fichas/nova")}>Nova ficha</Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          label="Buscar por nome, CPF, quem cadastrou, nº inscrito ou ID"
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && carregar()}
          sx={{ width: 360 }}
        />
        <Button variant="outlined" onClick={carregar}>Buscar</Button>
        <TextField select size="small" label="Situação" value={filtro}
          onChange={(e) => setFiltro(e.target.value)} sx={{ width: 220 }}>
          <MenuItem value="todas">Todas</MenuItem>
          <MenuItem value="andamento">Em andamento</MenuItem>
          <MenuItem value="enviadas">Enviadas / concluídas</MenuItem>
        </TextField>
        <TextField select size="small" label="Entrega de documentos" value={entrega}
          onChange={(e) => setEntrega(e.target.value)} sx={{ width: 220 }}>
          <MenuItem value="todas">Todas</MenuItem>
          <MenuItem value="1">Pendente</MenuItem>
          <MenuItem value="2">Parcial</MenuItem>
          <MenuItem value="3">Completa</MenuItem>
        </TextField>
      </Stack>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Edição</TableCell>
              <TableCell>Responsável</TableCell>
              <TableCell>Situação</TableCell>
              <TableCell>Entrega</TableCell>
              <TableCell align="right">Receitas</TableCell>
              <TableCell align="right">Despesas</TableCell>
              <TableCell align="right">Renda per capita</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fichasFiltradas.map((f) => (
              <TableRow key={f.Id} hover>
                <TableCell>{f.Id}</TableCell>
                <TableCell>{f.Edicao}</TableCell>
                <TableCell>{f.Responsavel}</TableCell>
                <TableCell>{f.Situacao}</TableCell>
                <TableCell>{f.SituacaoEntrega || "—"}</TableCell>
                <TableCell align="right">{moeda(f.TotalReceitas)}</TableCell>
                <TableCell align="right">{moeda(f.TotalDespesas)}</TableCell>
                <TableCell align="right">{moeda(f.RendaPerCapita)}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" title="Relatório" onClick={() => window.open(`/fichas/${f.Id}/relatorio`, "_blank")}>
                    <DescriptionIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" title="Editar" onClick={() => navigate(`/fichas/${f.Id}`)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  {podeExcluir(f) ? (
                    <IconButton size="small" color="error" title="Excluir" onClick={() => excluir(f.Id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <Tooltip title="Ficha já enviada não pode ser excluída">
                      <span>
                        <IconButton size="small" disabled><DeleteIcon fontSize="small" /></IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {fichasFiltradas.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">Nenhuma ficha encontrada.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
